import { tokens } from 'src/design-system';
import { CHARGE_TYPE_LABELS } from './AdditionalCharges.constants';
import { AdditionalChargeApplicabilityLevel, AdditionalChargeType } from './AdditionalCharges.types';
import { AdditionalChargeLineStatus } from './AdditionalChargeGrids.types';

/**
 * Additional Vendor Charges — Part 2 tabbed grids copy, labels and gates (ISS-291749 / C2).
 */

/**
 * D2 — the Third-Party Reimbursement tab was hidden until A5 (ISS-291744) unblocked its GST/vendor
 * fields. Unblocked as of ISS-291743: the backend now serves those columns editable and accepts them
 * on `PATCH /lines/bulk`, so the tab is unhidden — no renderer work was needed, as anticipated.
 */
export const HIDDEN_CHARGE_TYPES: AdditionalChargeType[] = [];

/** Tab order and labels. Reuses the Part-1 display labels so the two halves never drift apart. */
export const TAB_LABELS = CHARGE_TYPE_LABELS;

export const ADDITIONAL_CHARGE_TAB_ORDER: AdditionalChargeType[] = [
  'INCIDENTAL',
  'SELF_REIMBURSEMENT',
  'THIRD_PARTY_REIMBURSEMENT',
];

/**
 * A line may only be edited while in one of these statuses (contract §3.3, per PR #4386's terminal-vs.
 * -editable model). DRAFT is a fresh, never-submitted line; REWORK was sent back by the approver, so the
 * vendor edits and re-submits it. APPROVED and REJECTED are both TERMINAL — REJECTED is deliberately NOT
 * here (ISS-292248 review fix, CRITICAL 2): it used to be, but the backend's bulk-PATCH throws "not
 * mutable in status REJECTED" for a rejected line, so including it meant `buildSaveLines` re-sent it on
 * every Save and the server rejected the WHOLE batch — one rejected line bricked Save for every other
 * line in the task. A rejected line is done; only a REWORK line (the approver's explicit "fix this and
 * try again") is editable again.
 */
export const EDITABLE_LINE_STATUSES: AdditionalChargeLineStatus[] = ['DRAFT', 'REWORK'];

/**
 * Lines in these statuses can no longer be sent for approval, so their select checkbox is disabled.
 * Applies to every vendor type's additional-charge grid (this grid is shared across all of them).
 * REWORK is intentionally excluded — a reworked line can be re-sent for approval.
 */
export const NON_SUBMITTABLE_LINE_STATUSES: AdditionalChargeLineStatus[] = ['IN_PROCESS', 'APPROVED', 'REJECTED'];

export const LINE_STATUS_LABELS: Record<AdditionalChargeLineStatus, string> = {
  DRAFT: 'Draft',
  IN_PROCESS: 'In Process',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  REWORK: 'Rework',
};

/**
 * Runtime status → colour lookup. Read from a map at render time (`style={{ color: STATUS_COLORS[x] }}`)
 * — the one inline-style case CLAUDE.md allows, since the value is not statically known.
 */
export const LINE_STATUS_COLORS: Record<AdditionalChargeLineStatus, string> = {
  DRAFT: tokens.colors.textSecondary,
  IN_PROCESS: tokens.colors.primary,
  APPROVED: tokens.colors.success,
  REJECTED: tokens.colors.error,
  REWORK: tokens.colors.warning,
};

/** Display vs. wire format for the two date columns (`blDate`, `serviceStartDate`). Wire dates are ISO. */
export const ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT = 'DD-MM-YYYY';
export const ADDITIONAL_CHARGE_DATE_WIRE_FORMAT = 'YYYY-MM-DD';

/**
 * The section sub-heading beside each charge name. SAC is deliberately NOT here — it is a line-level
 * grid column (`entityColumn: 'sacCode'`) per the design, and repeating it on the header duplicated it.
 */
export const APPLICABILITY_LEVEL_LABELS: Record<AdditionalChargeApplicabilityLevel, string> = {
  BL: 'BL Level',
  CONTAINER: 'Container Level',
};

/** Sub-heading beneath each charge name (the mock's "BL-wise" / "Container-wise" descriptor). */
export const APPLICABILITY_WISE_LABELS: Record<AdditionalChargeApplicabilityLevel, string> = {
  BL: 'BL-wise',
  CONTAINER: 'Container-wise',
};

export const ADDITIONAL_CHARGE_GRID_LABELS = {
  /** Returns to Part 1 to add/remove declared charges. */
  editCharges: 'Edit charges',
  saveAsDraft: 'Save as Draft',
  sendForApproval: 'Send for Approval',
  uploadAttachmentTooltip: 'Upload attachment',
  attachmentRequiredTooltip: 'Attachment required',
  replaceAttachment: 'Replace',
  ratePlaceholder: 'Enter rate',
  datePlaceholder: 'Select date',
  /** ISS-292248: shown in place of a converted INR figure when the currency IS the line's persisted one
   * and there is genuinely no exchange rate as of the PCD (`rateStatus: 'unavailable'`). */
  rateUnavailable: 'Rate unavailable',
  rateUnavailableTooltip:
    'No exchange rate is available for this currency as of the PCD. The line cannot be submitted until rates exist.',
  /** ISS-292248 review fix (CRITICAL 1): shown instead of the above when the vendor just picked a
   * DIFFERENT currency than what's persisted (`rateStatus: 'pending-refresh'`) — `exchangeRates` is only
   * ever seeded from currencies already on the lines, so the map simply predates this choice. This is
   * NOT a blocked-submit state: Submit fetches its own rate server-side. */
  ratePendingRefresh: 'Rate pending refresh',
  ratePendingRefreshTooltip: 'Exchange rate will be fetched when you save.',
  /** Shown when the conversion is impossible because the task has no Price Calculation Date yet — the
   * rate is dated BY the PCD, so without one there is nothing to convert at. Distinguishing this from
   * `rateUnavailable` matters: blaming the currency when the real gap is an uncaptured upstream date
   * sends the vendor looking in the wrong place (ISS-292248). */
  pcdMissing: 'PCD not available',
  pcdMissingTooltip:
    'The Price Calculation Date for this task has not been captured yet, so amounts cannot be converted to INR. It is derived from an earlier task (e.g. the BL date on Upload Draft BL for FF, or the BOE date for CHA).',
  /** ISS-292248: heading above the per-currency exchange-rate footer beneath the grids. */
  exchangeRatesHeading: 'Exchange Rates',
  /** ISS-292248 Task 4: prefixes the computed value shown alongside an overridden quantity dim, e.g.
   * "(Computed: 5)" — so the vendor can see what they overrode without losing the original figure. */
  quantityDimComputedPrefix: 'Computed',
};

export const ADDITIONAL_CHARGE_GRID_MESSAGES = {
  fetchLinesFailed: 'Unable to load the additional charge details.',
  fetchColumnConfigFailed: 'Unable to load the additional charge column configuration.',
  saveDraftFailed: 'Unable to save the additional charges.',
  saveDraftSuccess: 'Additional charges saved as draft.',
  submitFailed: 'Unable to send the selected charges for approval.',
  submitSuccess: 'Selected charges sent for approval.',
  noRowsSelected: 'Please select at least one row to send for approval.',
  /**
   * ISS-292248 Task 5: the client-side pre-check in `onSubmitForApproval` blocks Send for Approval
   * before the API call when a TICKED line still carries a rate-engine error or an unpinned
   * open-ended quantity dim (`isLineBlockedFromSubmit`) — names the first such line and its first
   * problem so the vendor knows exactly which row to fix.
   */
  lineBlockedFromSubmit: (lineReferenceNumber: string, detail: string): string =>
    `${lineReferenceNumber} cannot be sent for approval: ${detail}`,
  /** Fallback detail when a line is blocked purely by an unpinned open-ended quantity dim, which
   * carries no line-level error message of its own (`lineErrors` is empty in that case). */
  lineBlockedOpenEndedQuantity: 'its quantity has an open-ended dimension that has not been pinned yet.',
  uploadFailed: 'Unable to upload the attachment.',
  noChargesDeclared: 'No additional charges have been declared for this task yet.',
  /**
   * Fan-out is one line per BL / per container, so a charge declared on a shipment that has neither
   * produces no rows. Rendered as the grid's own `emptyText` so the columns stay visible and the section
   * reads as an empty grid rather than a wall of prose.
   */
  noBlFound: 'No BL found',
  noContainersFound: 'No containers found',
};
